import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiMessageCircle, FiPhone, FiVideo, FiSettings, FiZap } from 'react-icons/fi';
import logo from '../../Images/logo.png';
import styles from './PricingPage.module.css';

const PricingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.container}>
      {/* Ambient glows */}
      <div className={styles.ambientGlows}>
        <div className={styles.glow1}></div>
        <div className={styles.glow2}></div>
        <div className={styles.glow3}></div>
      </div>

      {/* 🟦 1. HEADER / NAVIGATION */}
      <header className={styles.header} style={{ flex: "none" }}>
        <div className={styles.headerInner}>
          <div className={styles.logoArea} onClick={() => navigate('/')}>
            <img src={logo} alt="SaleSense Logo" className={styles.logoImg} />
            <span className={styles.logoText}>Sailsense <span className={styles.logoCyan}>AI</span></span>
          </div>
          <nav className={styles.navLinks}>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <a href="/ai-agents" onClick={(e) => { e.preventDefault(); navigate('/ai-agents'); }}>AI Agents</a>
            <a href="/platform" onClick={(e) => { e.preventDefault(); navigate('/platform'); }}>Platform</a>
            <a href="/sales-operations" onClick={(e) => { e.preventDefault(); navigate('/sales-operations'); }}>Sales Operations</a>
            <a href="/solutions" onClick={(e) => { e.preventDefault(); navigate('/solutions'); }}>Solutions</a>
            <a href="/pricing" className={styles.activeLink} onClick={(e) => { e.preventDefault(); }}>Pricing</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a>
          </nav>
          <div className={styles.headerActions}>
            <button className={styles.trialBtn} onClick={() => navigate('/login')}>
              login <FiArrowRight />
            </button>
          </div>
        </div>
      </header>

      {/* Grid line overlay */}
      <div className={styles.gridOverlay}></div>

      {/* 🟦 2. PRICING INTRO */}
      <section className={styles.sectionBlock}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            Pricing
          </div>
          <h1 className={styles.sectionCenterTitle}>
            Simple plans. <span className={styles.cyanTitle}>Outsized results.</span>
          </h1>
          <p className={styles.sectionSubtitle}>
            Pick a plan that fits your team today. Scale your AI workforce as your pipeline grows.
          </p>
        </div>

        <div className={styles.pricingGrid}>
          {/* STARTER */}
          <div className={styles.priceCard}>
            <div className={styles.priceHeader}>
              <span className={styles.planTitleStarter}>STARTER</span>
              <div className={styles.priceAmountBox}>
                <span className={styles.dollarSign}>$</span>
                <span className={styles.priceVal}>199</span>
                <span className={styles.pricePeriod}>/mo</span>
              </div>
              <p className={styles.planDesc}>For small teams testing AI sales.</p>
            </div>
            <ul className={styles.planList}>
              <li><FiCheck className={styles.checkIcon} /> Sailsense Chatbot (Website)</li>
              <li><FiCheck className={styles.checkIcon} /> 1,000 conversations / mo</li>
              <li><FiCheck className={styles.checkIcon} /> Basic dashboard</li>
              <li><FiCheck className={styles.checkIcon} /> Email support</li>
            </ul>
            <button className={styles.planBtnOutline} onClick={() => navigate('/login')}>
              Start Free Trial <FiArrowRight />
            </button>
          </div>

          {/* GROWTH (Highlighted) */}
          <div className={styles.priceCardFeatured}>
            <div className={styles.featuredBadgeBox}>
              <span className={styles.featuredBadge}><FiZap style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} /> Most Popular</span>
            </div>
            <div className={styles.priceHeader}>
              <span className={styles.planTitleGrowth}>GROWTH</span>
              <div className={styles.priceAmountBox}>
                <span className={styles.dollarSign}>$</span>
                <span className={styles.priceVal}>599</span>
                <span className={styles.pricePeriod}>/mo</span>
              </div>
              <p className={styles.planDesc}>Most popular for dealerships & SMBs.</p>
            </div>
            <ul className={styles.planList}>
              <li><FiCheck className={styles.checkIcon} /> Chatbot + WhatsApp</li>
              <li><FiCheck className={styles.checkIcon} /> ROVIA Voice Agent (1,000 mins)</li>
              <li><FiCheck className={styles.checkIcon} /> AIMEE Video Presenter</li>
              <li><FiCheck className={styles.checkIcon} /> CRM integrations</li>
              <li><FiCheck className={styles.checkIcon} /> Advanced analytics</li>
              <li><FiCheck className={styles.checkIcon} /> Priority support</li>
            </ul>
            <button className={styles.planBtnSolid} onClick={() => navigate('/login')}>
              Get Growth <FiArrowRight />
            </button>
          </div>

          {/* ENTERPRISE */}
          <div className={styles.priceCard}>
            <div className={styles.priceHeader}>
              <span className={styles.planTitleEnterprise}>ENTERPRISE</span>
              <div className={styles.priceAmountBox}>
                <span className={styles.priceValText}>Custom</span>
              </div>
              <p className={styles.planDesc}>For multi-location & OEM networks.</p>
            </div>
            <ul className={styles.planList}>
              <li><FiCheck className={styles.checkIcon} /> Unlimited agents & channels</li>
              <li><FiCheck className={styles.checkIcon} /> Dedicated AIMEE avatars</li>
              <li><FiCheck className={styles.checkIcon} /> SSO & role-based access</li>
              <li><FiCheck className={styles.checkIcon} /> Custom workflows & integrations</li>
              <li><FiCheck className={styles.checkIcon} /> Dedicated success manager</li>
              <li><FiCheck className={styles.checkIcon} /> SLA + compliance</li>
            </ul>
            <button className={styles.planBtnOutline} onClick={() => navigate('/login')}>
              Talk to Sales <FiArrowRight />
            </button>
          </div>
        </div>
      </section>

      {/* 🟦 3. FAQ SECTION */}
      <section className={styles.sectionBlock} style={{ borderTop: "1px solid rgba(255, 255, 255, 0.05)", paddingTop: "5rem", paddingBottom: "6rem" }}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            FAQ
          </div>
        </div>

        <div className={styles.faqWrapper}>
          <div className={styles.faqCard}>
            <h4 className={styles.faqQuestion}>Can I switch plans later?</h4>
            <p className={styles.faqAnswer}>Yes — upgrade, downgrade or cancel anytime.</p>
          </div>

          <div className={styles.faqCard}>
            <h4 className={styles.faqQuestion}>Do you offer custom integrations?</h4>
            <p className={styles.faqAnswer}>Enterprise plans include custom CRM, telephony and DMS integrations.</p>
          </div>

          <div className={styles.faqCard}>
            <h4 className={styles.faqQuestion}>Is there a free trial?</h4>
            <p className={styles.faqAnswer}>Yes — start with a 14-day free trial on the Starter plan.</p>
          </div>
        </div>
      </section>

      {/* 🟦 4. FOOTER */}
      <footer className={styles.footerSection}>
        <div className={styles.footerInner}>
          {/* Brand area left */}
          <div className={styles.footerBrandColumn}>
            <div className={styles.footerBrandLogo} onClick={() => navigate('/')}>
              <img src={logo} alt="SaleSense Logo" className={styles.footerLogoImg} />
              <span>Sailsense AI</span>
            </div>
            <p className={styles.footerBrandText}>
              AI Chat, Voice and Video agents that capture leads, automate conversations and close sales — 24/7.
            </p>
            <p className={styles.footerBrandProductOf}>
              A product of <strong>Provaantech</strong>
            </p>
          </div>

          {/* Brand System center */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Brand System</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>
                <FiMessageCircle style={{ marginRight: '0.5rem' }} /> Sailsense Chatbot
              </li>
              <li className={styles.footerLinkItem}>
                <FiPhone style={{ marginRight: '0.5rem' }} /> ROVIA — Voice AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiVideo style={{ marginRight: '0.5rem' }} /> AIMEE — Video AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiSettings style={{ marginRight: '0.5rem' }} /> Sailsense Platform
              </li>
            </ul>
          </div>

          {/* Industries right */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Focus Industry</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>Automotive Dealerships</li>
            </ul>
          </div>
        </div>

        {/* Bottom row copyright */}
        <div className={styles.footerBottomBar}>
          <div>© 2026 Sailsense AI - Provaantech. All rights reserved.</div>
          <div>Built for high-performance sales teams.</div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
